<template>
  <div
    v-if="pending || novelChapters?.data.length"
    class="space-y-4 lg:space-y-6"
  >
    <div v-if="!noLink" class="flex items-center">
      <h1 class="text-xl sm:text-2xl lg:text-3xl font-bold flex-grow">
        {{ $t("components.NamiLatestUpdatesNovels.heading") }}
      </h1>
      <!--      <NamiButton-->
      <!--        button-type="secondary"-->
      <!--        text-->
      <!--        pill-->
      <!--        :icon="IconArrowRight"-->
      <!--        :href="`${locale}/updates`"-->
      <!--      />-->
    </div>
    <div v-else class="flex items-center">
      <h1 class="text-xl sm:text-2xl lg:text-3xl font-bold flex-grow">
        {{ $t("components.namiLatestUpdates.heading") }}
      </h1>
    </div>
    <div
      v-if="pending || titlesPending"
      class="grid responsive-gap grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 3xl:grid-cols-4"
    >
      <Skeleton v-for="_ in items" class="h-56 w-full" />
    </div>
    <div v-else-if="error" class="text-red-500">
      {{ error }}
    </div>
    <!-- grid display -->
    <ul
      v-else-if="mappedNovelChapters.length"
      role="list"
      class="grid responsive-gap grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 3xl:grid-cols-4"
    >
      <li
        v-for="{ title, chapters, organization } in mappedNovelChapters"
        :class="{ 'lg:last:hidden 2xl:last:grid': items % 2 !== 0 }"
        class="overflow-hidden responsive-gap grid grid-cols-[8rem_1fr] sm:grid-cols-[10rem_1fr] tile p-0"
      >
        <TheNuxtLink :to="linkTo(title)" class="block">
          <TitleCover :title="title" class="h-full shadow-md" res="512" />
        </TheNuxtLink>
        <div
          class="min-w-[0] w-full grid grid-rows-[auto_1fr] py-2 pr-2 md:pb-4 md:pr-4"
        >
          <TheNuxtLink :to="linkTo(title)" class="mb-2">
            <h3
              class="line-clamp-1 sm:line-clamp-2 sm:text-lg font-semibold"
              style="word-break: break-word"
            >
              {{ extractFromLocale(locale, title.attributes.title) }}
            </h3>
          </TheNuxtLink>
          <div class="">
            <template
              v-for="(chapter, index) in chapters.slice(0, 2)"
              :key="chapter.id"
            >
              <NamiDivider v-if="index > 0" class="!my-2" />
              <NovelChapterCard
                class="x-small transition-colors canhover:hover:bg-neutral-200 canhover:dark:hover:bg-neutral-700 active:bg-neutral-200 dark:active:bg-neutral-700"
                :chapter="chapter"
                :organization="organization"
                :title-cover-url="selectLocalizedCover(locale, title, '512')"
                :title-id="title.id"
                :is-locked="!(novelGuestGating?.[chapter.id] ?? true)"
                :is-access="novelUserGating?.[chapter.id] ?? false"
                no-stats
                transparent
                small
              />
            </template>
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import {
  getRelationship,
  isTitle,
  NovelChapter,
  type NovelChapterEntity,
  type OrganizationRelation,
  type PopulateRelationship,
  selectLocalizedCover,
  type TitleEntity,
} from "~/src/api";
import { useQuery } from "@tanstack/vue-query";
import { getAllTitles } from "~/src/api/utils/title";

const nuxtApp = useNuxtApp();
const { locale } = nuxtApp.$i18n.global;
const settingsStore = nuxtApp.$settings();
const isLoggedIn = useIsLoggedIn();

const userLanguages = computed(
  () => settingsStore?.settings.platform.preferredTranslationLocales ?? [],
);

const { items } = defineProps<{
  noLink?: boolean;
  items: number;
}>();

const batchLimit = 100;

const {
  data: novelChapters,
  suspense: chapterPrefetch,
  error,
  isPending: pending,
} = useQuery({
  queryKey: ["latest-updates-novels", { items, userLanguages }],
  queryFn: async () => {
    const uniqueTitleIds = new Set<string>();
    if (items > 0) {
      let res = await NovelChapter.find({
        translatedLanguages:
          userLanguages.value.length > 0 ? userLanguages.value : ALL_LANGUAGES,
        order: {
          firstPublishedAt: "desc",
        },
        limit: batchLimit,
      });

      res.data.forEach((ch) =>
        uniqueTitleIds.add(getRelationship(ch, "title").id),
      );

      while (uniqueTitleIds.size < items && res.meta.offset < batchLimit * 3) {
        const paginated = await NovelChapter.find({
          translatedLanguages:
            userLanguages.value.length > 0
              ? userLanguages.value
              : ALL_LANGUAGES,
          order: {
            firstPublishedAt: "desc",
          },
          offset: res.meta.offset + res.meta.limit,
          limit: res.meta.limit,
        });

        paginated.data.forEach((ch) =>
          uniqueTitleIds.add(getRelationship(ch, "title").id),
        );
        res.data.push(...paginated.data);
        res.meta = paginated.meta;
      }

      return res;
    }
    return {
      type: "collection",
      data: [],
      meta: {
        total: 0,
        limit: 0,
        offset: 0,
      },
    };
  },
});
const {
  data: novelTitles,
  suspense: titlePrefetch,
  isPending: titlesPending,
} = useQuery({
  queryKey: ["latest-updates-titles", { items: items, userLanguages }],
  queryFn: async () => {
    const titleIds = new Set<string>();
    novelChapters.value?.data.forEach((chapter) => {
      const id = chapter.relationships.find(isTitle)?.id;
      if (id) titleIds.add(id);
    });

    if (!titleIds.size) return [];
    return await getAllTitles(Array.from(titleIds));
  },
  enabled: () => !!novelChapters.value || !!error.value,
});

const mappedNovelChapters = computed(() => {
  if (!novelChapters.value?.data.length || !novelTitles.value?.length)
    return [];
  const titlesArr: {
    title: TitleEntity;
    organization: PopulateRelationship<OrganizationRelation>;
    chapters: NovelChapterEntity[];
  }[] = [];

  novelChapters.value.data.forEach((chapter) => {
    const titleId = chapter.relationships.find(isTitle)?.id;
    if (!titleId) return;
    const foundTitle = titlesArr.find(({ title }) => title.id === titleId);
    const titleEntity = novelTitles.value.find((t) => t.id === titleId)!;
    if (!foundTitle && titleEntity) {
      titlesArr.push({
        title: titleEntity,
        organization: getRelationship(titleEntity, "organization", true),
        chapters: [chapter],
      });
    } else if (foundTitle) {
      foundTitle.chapters.push(chapter);
    }
  });

  return titlesArr.slice(0, items);
});

const novelChapterIds = computed(
  () => novelChapters.value?.data.map((ch) => ch.id) ?? [],
);

const { data: novelGuestGating, suspense: lockPrefetch } = useQuery(
  useGatingAccess(novelChapterIds, "novel_chapter"),
);
const { data: novelUserGating } = useQuery({
  ...useGatingAccess(novelChapterIds, "novel_chapter", getTokenOrThrow),
  enabled: isLoggedIn,
});

onServerPrefetch(async () => {
  await chapterPrefetch();
  await titlePrefetch();
  await lockPrefetch();
});
</script>
